// src/hooks/usePermissions.js
// Create this file if it doesn't exist

import { useSelector } from 'react-redux';
import { selectUser } from '../../store/slices/authSlice';

export const usePermissions = () => {
    const user = useSelector(selectUser);

    const hasPermission = (permission) => {
        if (!user || !user.permissions) return false;

        // Check if user has the specific permission
        return user.permissions.some(p =>
            typeof p === 'string' ? p === permission : p.name === permission
        );
    };

    const hasRole = (role) => {
        if (!user || !user.roles) return false;

        // Check if user has the specific role
        return user.roles.some(r =>
            typeof r === 'string' ? r === role : r.role_name === role
        );
    };

    const hasAnyPermission = (permissions) => {
        if (!permissions || !Array.isArray(permissions)) return false;
        return permissions.some(permission => hasPermission(permission));
    };

    const hasAnyRole = (roles) => {
        if (!roles || !Array.isArray(roles)) return false;
        return roles.some(role => hasRole(role));
    };

    // Menu-based permission helpers
    const canViewMenu = (menuName) => {
        if (!user || !user.permissions) return false;
        const viewPermission = `${menuName}:view`;
        return hasPermission(viewPermission);
    };

    const canCreateOnMenu = (menuName) => {
        if (!user || !user.permissions) return false;
        const createPermission = `${menuName}:create`;
        return hasPermission(createPermission);
    };

    const canUpdateOnMenu = (menuName) => {
        if (!user || !user.permissions) return false;
        const updatePermission = `${menuName}:update`;
        return hasPermission(updatePermission);
    };

    const canDeleteOnMenu = (menuName) => {
        if (!user || !user.permissions) return false;
        const deletePermission = `${menuName}:delete`;
        return hasPermission(deletePermission);
    };

    const canPerformActionOnMenu = (menuName, action) => {
        if (!user || !user.permissions) return false;
        const permission = `${menuName}:${action}`;
        return hasPermission(permission);
    };

    return {
        hasPermission,
        hasRole,
        hasAnyPermission,
        hasAnyRole,
        canViewMenu,
        canCreateOnMenu,
        canUpdateOnMenu,
        canDeleteOnMenu,
        canPerformActionOnMenu,
        user,
        permissions: user?.permissions || [],
        roles: user?.roles || [],
    };
};

// Alternative simple version if you don't have complex auth context
export const usePermissionsSimple = () => {
    // For now, return true for all permissions to avoid blocking UI
    // Replace this with actual permission logic later
    const hasPermission = () => true;
    const hasRole = () => true;
    const hasAnyPermission = () => true;
    const hasAnyRole = () => true;

    return {
        hasPermission,
        hasRole,
        hasAnyPermission,
        hasAnyRole,
    };
};